import {
  InvitationsApi,
  CreateInvitationRequest,
  ResendInvitationRequest,
  InvitationResponse,
  InvitationListResponse,
  ValidateInvitationTokenResponse,
  AcceptInvitationResponse,
  Configuration
} from 'we-tree-api-client/v2';

import { getApiConfiguration } from '@/services/SessionService';
import { ApiResult, withApiSetup, safeApiCall } from './apiWrapper';
import { ConfigurationError, WeTreeApiError } from './errors';
import appConfig from '@/config/app';

/**
 * Wrapped invitations API with standardized error handling
 */
class WrappedInvitationsApi {
  /**
   * Create a new invitation
   */
  async createInvitation(request: CreateInvitationRequest): Promise<ApiResult<InvitationResponse>> {
    return withApiSetup(
      (config) => new InvitationsApi(config),
      getApiConfiguration,
      (api) => api.createInvitation(request)
    );
  }

  /**
   * Get invitations for a family
   */
  async getInvitations(familyId: string): Promise<ApiResult<InvitationListResponse>> {
    return withApiSetup(
      (config) => new InvitationsApi(config),
      getApiConfiguration,
      (api) => api.getInvitations(familyId)
    );
  }

  /**
   * Validate an invitation token (public endpoint - no auth required)
   */
  async validateInvitationToken(token: string): Promise<ApiResult<ValidateInvitationTokenResponse>> {
    try {
      // For public endpoints, create a configuration without an access token
      // Try to get authenticated config first, but fall back to unauthenticated config
      let configuration = await getApiConfiguration();
      if (!configuration) {
        // Create a configuration without authentication for public endpoints
        configuration = new Configuration({
          basePath: appConfig.apiUrl
          // No accessToken - this is a public endpoint
        });
      }

      const api = new InvitationsApi(configuration);
      return await safeApiCall(() => api.validateInvitationToken(token));
    } catch (error) {
      return {
        success: false,
        error: error instanceof WeTreeApiError ? error : new ConfigurationError('Failed to validate invitation token')
      };
    }
  }

  /**
   * Accept an invitation
   */
  async acceptInvitation(token: string): Promise<ApiResult<AcceptInvitationResponse>> {
    return withApiSetup(
      (config) => new InvitationsApi(config),
      getApiConfiguration,
      (api) => api.acceptInvitation({ token })
    );
  }

  /**
   * Cancel an invitation
   */
  async cancelInvitation(invitationId: string): Promise<ApiResult<void>> {
    return withApiSetup(
      (config) => new InvitationsApi(config),
      getApiConfiguration,
      (api) => api.cancelInvitation(invitationId)
    );
  }

  /**
   * Resend an invitation email
   */
  async resendInvitation(invitationId: string, body?: ResendInvitationRequest): Promise<ApiResult<InvitationResponse>> {
    return withApiSetup(
      (config) => new InvitationsApi(config),
      getApiConfiguration,
      (api) => api.resendInvitation(invitationId, body)
    );
  }

  /**
   * Delete an invitation permanently
   */
  async deleteInvitation(invitationId: string): Promise<ApiResult<void>> {
    return withApiSetup(
      (config) => new InvitationsApi(config),
      getApiConfiguration,
      (api) => api.deleteInvitation(invitationId)
    );
  }
}

/**
 * Wrapped invitations API instance with standardized error handling
 */
export const invitationsApi = new WrappedInvitationsApi();

export default invitationsApi;
